import { handOverDraft } from "./draftHandoff";
import { handOverPriceList } from "./priceListHandoff";
import type { ProposedPriceLine } from "./priceList";
import type { FleetItem } from "./fleetList";

/**
 * What comes next once a record Ask helped start is saved — the compound
 * cases (user, 2026-10-14: "a client and contacts in it, a new provider with a
 * price sheet"): a new provider's price list or fleet list opens on its sheet,
 * a new client's or provider's contact opens filled in and linked to it. Held
 * in memory only; nothing is saved by these steps — each opens a form or a
 * sheet, unsaved, for the person to check and save.
 */
export type AfterCreateStep =
    | { type: "price_list"; text?: string; file?: File; lines?: ProposedPriceLine[]; places?: string[] }
    | { type: "fleet_list"; text?: string; file?: File; items?: FleetItem[] }
    | { type: "contact"; text?: string; file?: File };

export type CreatedKind = "provider" | "client" | "company";

let queued: { kind: CreatedKind; steps: AfterCreateStep[] } | null = null;

/** Queue the steps for the next record of this kind that's created. */
export const queueAfterCreate = (kind: CreatedKind, steps: AfterCreateStep[]): void => {
    queued = steps.length ? { kind, steps } : null;
};

/** The steps waiting for the next record of this kind, without taking them (for the note on its page). */
export const peekAfterCreate = (kind: CreatedKind): AfterCreateStep[] => (queued?.kind === kind ? queued.steps : []);

/** What happens once the record is saved, in words: "its rate sheet opens with the price list". */
export const afterCreateNote = (steps: AfterCreateStep[]): string | null => {
    const parts = steps.map((step) => (step.type === "price_list" ? "its rate sheet opens with the price list"
        : step.type === "fleet_list" ? "its assets open with the fleet list" : "a new contact opens, linked to it"));
    return parts.length ? `Save it, and ${parts.join(", then ")} — nothing is lost.` : null;
};

/** The steps waiting for a record of this kind, once (cleared when taken). */
export const takeAfterCreate = (kind: CreatedKind): AfterCreateStep[] => {
    if (!queued || queued.kind !== kind) return [];
    const { steps } = queued;
    queued = null;
    return steps;
};

/**
 * Hand each step to the page that does it, linked to the record just saved.
 * @returns Where to go first (the record's page when there's nothing to do),
 *   and a note for what's left to open after (a contact after a price list).
 */
export const runAfterCreate = (
    kind: CreatedKind,
    record: { organizationId: number; name?: string | null },
    steps: AfterCreateStep[],
): { path: string; next: string | null } => {
    const id = record.organizationId;
    const paths: string[] = [];
    steps.forEach((step) => {
        if (step.type === "price_list" && kind === "provider") {
            handOverPriceList({ sheet: "provider", recordId: id, text: step.text, file: step.file, lines: step.lines, places: step.places });
            paths.push(`/provider/${id}?tab=services`);
        } else if (step.type === "fleet_list" && kind === "provider") {
            handOverDraft("fleet_list", id, { text: step.text, file: step.file, items: step.items });
            paths.push(`/provider/${id}?tab=assets`);
        } else if (step.type === "contact") {
            handOverDraft("contact_paste", 0, { text: step.text, file: step.file, organization: { organizationId: id, name: record.name ?? "", type: kind } });
            paths.push("/contact");
        }
    });
    const first = paths[0] ?? `/${kind}/${id}`;
    const contactLater = paths.length > 1 && paths.slice(1).includes("/contact");
    return { path: first, next: contactLater ? "The contact is ready too — open Contacts › Create to check and save it." : null };
};
